import type { Event, Store, StoreWritable } from 'effector'

import { sample } from 'effector'
import { model as router } from '~/core/router'
import { checkIsSearchQueryValid } from '~/shared/helpers'
import {
  debouncedAutocompleteTyping,
  getSearchAutocomplete,
  pickAutocompleteItem,
  pushAutocompleteTyping,
  resetAutocomplete,
} from './model'

export type SearchAutocompleteRouteBinding = {
  $searchValue: StoreWritable<string>
  /** When true, autocomplete wiring runs for this route */
  $isActive: Store<boolean>
  /** Fires on each keystroke in the search field (debounced fetch) */
  typingClock: Event<unknown>
  /** Reset suggestions (leave page, clear field, hybrid hint, …) */
  resetAutocompleteOn: Event<unknown>[]
  /** `router.navigatePush` target when user picks a suggestion (`query` is trimmed). */
  getNavigateToOnPick: (query: string) => router.ToEx
  /** Injected from the route: voice result ready (e.g. `features/voice-search`). */
  voiceSearchResultClock: Event<string>
}

/**
 * Shared autocomplete wiring for hybrid / regular search routes.
 */
export function bindSearchAutocompleteRoute({
  $searchValue,
  $isActive,
  typingClock,
  resetAutocompleteOn,
  getNavigateToOnPick,
  voiceSearchResultClock,
}: SearchAutocompleteRouteBinding): void {
  sample({
    clock: voiceSearchResultClock,
    source: $isActive,
    filter: (isActive) => isActive,
    target: resetAutocomplete,
  })

  sample({
    clock: typingClock,
    target: pushAutocompleteTyping,
  })

  sample({
    clock: resetAutocompleteOn,
    target: resetAutocomplete,
  })

  sample({
    clock: $searchValue,
    filter: (q: string) => !checkIsSearchQueryValid(q),
    target: resetAutocomplete,
  })

  sample({
    clock: pickAutocompleteItem,
    source: $isActive,
    filter: (onPage) => onPage,
    target: resetAutocomplete,
  })

  sample({
    clock: pickAutocompleteItem,
    source: $isActive,
    filter: (onPage) => onPage,
    fn: (_onPage, { suggestionText }) => suggestionText.trim(),
    target: $searchValue,
  })

  sample({
    clock: pickAutocompleteItem,
    source: $isActive,
    filter: (onPage) => onPage,
    fn: (_onPage, { suggestionText }) =>
      getNavigateToOnPick(suggestionText.trim()),
    target: router.navigatePush,
  })

  sample({
    clock: debouncedAutocompleteTyping,
    source: $searchValue,
    filter: (q) => checkIsSearchQueryValid(q),
    target: getSearchAutocomplete,
  })
}
